import React, { useEffect, useMemo, useState } from "react";
import { useLocation, matchPath } from "react-router-dom";
import Seo from "./Seo";
import API from "../util/API";

const routeMeta = [
  { path: "/", title: "Shop International Products", description: "Shop international products with secure checkout and reliable delivery." },
  { path: "/about", title: "About", description: "Learn about Cherrfy and our cross-border ecommerce experience." },
  { path: "/contact", title: "Contact", description: "Contact Cherrfy support for help with orders, payments, shipping, and more." },
  { path: "/cart", title: "My Cart", description: "Review your cart items and proceed to checkout." },
  { path: "/checkout", title: "Checkout", description: "Secure checkout for your Cherrfy order.", noIndex: true },
  { path: "/shipping-address", title: "Shipping Address", description: "Enter your shipping address for delivery.", noIndex: true },
  { path: "/review-order", title: "Review Order", description: "Review your order before placing it.", noIndex: true },
  { path: "/success-order", title: "Order Success", description: "Order confirmation.", noIndex: true },
  { path: "/order/:id", title: "Order Details", description: "Order details.", noIndex: true },
  { path: "/login", title: "Login", description: "Log in to Cherrfy to track orders and manage your account.", noIndex: true },
  { path: "/register", title: "Create Account", description: "Create your Cherrfy account to shop faster and track orders.", noIndex: true },
  { path: "/forgot-password", title: "Reset Password", description: "Reset your Cherrfy account password.", noIndex: true },
  { path: "/terms-of-service", title: "Terms of Service", description: "Cherrfy Terms of Service." },
  { path: "/privacy-policy", title: "Privacy Policy", description: "Cherrfy Privacy Policy." },
  { path: "/account-deletion", title: "Account Deletion", description: "Request deletion of your Cherrfy account and associated personal data." },
  { path: "/track-order", title: "Track Order", description: "Track your Cherrfy parcel by order ID or global tracking number." },
  { path: "/categories", title: "Categories", description: "Browse all Cherrfy product categories and subcategories." },
  { path: "/category/:slug", title: "Category", description: "Browse products by category on Cherrfy." },
  { path: "/search/:id", title: "Search", description: "Search products on Cherrfy." },
  { path: "/product/:id", title: "Product", description: "View product details on Cherrfy." },
  { path: "/products/:id", title: "Product", description: "View product details on Cherrfy." },
  { path: "/profile/*", title: "My Account", description: "Manage your Cherrfy account, orders, and settings.", noIndex: true },
];

export default function SeoRouter() {
  const location = useLocation();
  const [globalMeta, setGlobalMeta] = useState(null);
  const apiOrigin = useMemo(() => {
    const base = String(API.defaults?.baseURL || "").replace(/\/+$/, "");
    if (base.endsWith("/api")) return base.slice(0, -4);
    return base;
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadGlobal = async () => {
      try {
        const res = await API.get("/seo-meta/global", { headers: { Authorization: "" } });
        if (cancelled) return;
        setGlobalMeta(res.data?.data || null);
      } catch {
        if (!cancelled) setGlobalMeta(null);
      }
    };

    loadGlobal();
    return () => {
      cancelled = true;
    };
  }, []);

  const meta = useMemo(() => {
    const pathname = location.pathname;

    if (pathname === "/" && globalMeta) {
      const fallbackImage = apiOrigin ? `${apiOrigin}/uploads/seo-meta/seo-meta.jpg` : undefined;
      return {
        title: globalMeta.title || undefined,
        description: globalMeta.description || undefined,
        canonical: globalMeta.canonical_url || undefined,
        robots: globalMeta.robots || undefined,
        ogTitle: globalMeta.og_title || undefined,
        ogDescription: globalMeta.og_description || undefined,
        ogImage: globalMeta.og_image || fallbackImage,
        ogType: globalMeta.og_type || undefined,
        twitterCard: globalMeta.twitter_card || undefined,
        twitterTitle: globalMeta.twitter_title || undefined,
        twitterDescription: globalMeta.twitter_description || undefined,
        twitterImage: globalMeta.twitter_image || fallbackImage,
        jsonLd: globalMeta.json_ld || null,
        useDefaults: false,
      };
    }

    // Prefer the first matching route pattern.
    for (const entry of routeMeta) {
      if (matchPath({ path: entry.path, end: entry.path === "/" }, pathname)) {
        return entry;
      }
    }

    // Default fallback (unmatched route / 404).
    return {
      title: "404 - Page Not Found",
      description: "The page you requested could not be found on Cherrfy.",
      noIndex: true,
    };
  }, [location.pathname, globalMeta, apiOrigin]);

  if (location.pathname.startsWith("/product/") || location.pathname.startsWith("/products/")) {
    return null;
  }

  return (
    <Seo
      title={meta.title}
      description={meta.description}
      canonical={meta.canonical}
      robots={meta.robots}
      ogTitle={meta.ogTitle}
      ogDescription={meta.ogDescription}
      ogImage={meta.ogImage}
      ogType={meta.ogType}
      twitterCard={meta.twitterCard}
      twitterTitle={meta.twitterTitle}
      twitterDescription={meta.twitterDescription}
      twitterImage={meta.twitterImage}
      jsonLd={meta.jsonLd}
        useDefaults={meta.useDefaults}
      noIndex={Boolean(meta.noIndex)}
      pathname={location.pathname}
      search={location.search}
    />
  );
}
